'use client';
import { useAuth } from '@clerk/nextjs';
import { useViewerPlan } from '@hooks/useViewerPlan';
import { useConfig } from '@marine/configs';
import styles from '@styles/TryGoldButton.module.scss';
import cn from 'classnames';
import { ArrowRight } from 'lucide-react';
import { FC, Suspense, useMemo } from 'react';

type Props = {
  url?: string;
  icon?: boolean;
  text?: string | null;
  className?: string;
};

const TryGoldButtonContent: FC<Props> = ({ icon = false, className }) => {
  const { theboatAppUrl } = useConfig();

  const { isSignedIn } = useAuth();
  const { isSubscribed, isTrialUser } = useViewerPlan();

  const manageSubscriptionUrl = useMemo(() => `${theboatAppUrl}/settings/subscription`, [theboatAppUrl]);

  if (isTrialUser || isSubscribed) {
    return null;
  }

  if (!isSignedIn) {
    return (
      <a href={manageSubscriptionUrl} className={cn(styles.button, className)}>
        Try Pro for Free {icon && <ArrowRight />}
      </a>
    );
  }

  return (
    <a href={manageSubscriptionUrl} className={cn(styles.button, className)}>
      Upgrade {icon && <ArrowRight />}
    </a>
  );
};

/**
 * Wrapped in its own Suspense boundary at the definition so every call site inherits it.
 *
 * The button's state depends on Clerk auth and the viewer's plan — neither of which can be
 * prerendered. Without a boundary here, Cache Components
 * refuses to put the *containing page* in the static shell, which is what left pages blank for
 * clients that don't run JavaScript.
 */
const TryGoldButton: FC<Props> = (props) => (
  <Suspense fallback={null}>
    <TryGoldButtonContent {...props} />
  </Suspense>
);

export default TryGoldButton;
